import { useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FileQuestion, Home, Search, TrendingUp, Calculator } from 'lucide-react';
import { Link } from 'react-router-dom';
import { MobileBannerAd } from '@/components/shared/AdSense';
import AdFrame from '@/components/shared/AdFrame';
import SearchDropdown from '@/components/search/SearchDropdown';

const NotFound = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname
    );
    
    // Track 404 errors in analytics
    if (typeof window !== 'undefined' && (window as unknown).gtag) {
      (window as unknown).gtag('event', 'page_not_found', {
        event_category: 'errors',
        path: location.pathname,
        referrer: document.referrer,
      });
    }
  }, [location.pathname]);

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <>
      <Helmet>
        <title>Page Not Found - Wage Atlas | 404 Error</title>
        <meta name="description" content="The page you're looking for doesn't exist. Search for salary data or browse our tools." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-2xl w-full space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <FileQuestion className="h-8 w-8 text-muted-foreground" />
                <CardTitle className="text-2xl">404 - Page Not Found</CardTitle>
              </div>
              <CardDescription>
                The page you're looking for doesn't exist or has been moved. Use the search below to find what you need.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search functionality for revenue recovery */}
              <div className="p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Search className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm font-medium">Search for salary data:</p>
                </div>
                <SearchDropdown />
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={handleGoHome} variant="default" className="flex-1">
                  <Home className="mr-2 h-4 w-4" />
                  Go Home
                </Button>
                <Button asChild variant="outline" className="flex-1">
                  <Link to="/calculators">
                    <Calculator className="mr-2 h-4 w-4" />
                    Calculators
                  </Link>
                </Button>
              </div>

              {/* Revenue recovery CTAs */}
              <div className="mt-6 p-4 bg-primary/5 rounded-lg border border-primary/20">
                <p className="text-sm text-muted-foreground mb-3">
                  Popular pages you might be looking for:
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <Button asChild variant="secondary" size="sm">
                    <Link to="/trends">
                      <TrendingUp className="mr-2 h-4 w-4" />
                      Wage Trends
                    </Link>
                  </Button>
                  <Button asChild variant="secondary" size="sm">
                    <Link to="/best-states">Best States</Link>
                  </Button>
                  <Button asChild variant="secondary" size="sm">
                    <Link to="/no-degree-jobs">No-Degree Jobs</Link>
                  </Button>
                  <Button asChild variant="secondary" size="sm">
                    <Link to="/industry">Industry Center</Link>
                  </Button>
                  <Button asChild variant="secondary" size="sm">
                    <Link to="/methodology">Methodology</Link>
                  </Button>
                  <Button asChild variant="secondary" size="sm">
                    <Link to="/what-is-soc-code">SOC Code Guide</Link>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Ad placement */}
          <AdFrame className="flex justify-center">
            <MobileBannerAd />
          </AdFrame>
        </div>
      </div>
    </>
  );
};

export default NotFound;
